import React from "react";
import { MealPlan } from "../../services/api";
import { LLMMealPlanResultOutgoing } from "../../types";
import { getModelLabel, parseModelsFromUrl } from "../../utils/modelOptions";

interface ModelInfoContentProps {
    mealPlan: MealPlan;
    mealPlanData: LLMMealPlanResultOutgoing;
}

export const ModelInfoContent: React.FC<ModelInfoContentProps> = ({
    mealPlan,
    mealPlanData,
}) => {
    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "32px",
        maxWidth: "800px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
    };

    const sectionStyle: React.CSSProperties = {
        marginBottom: "32px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "20px",
        fontWeight: "600",
        color: "#1f2937",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };

    const cardStyle: React.CSSProperties = {
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "20px",
    };

    const labelStyle: React.CSSProperties = {
        fontSize: "14px",
        fontWeight: "600",
        color: "#374151",
        marginBottom: "8px",
    };

    const textareaStyle: React.CSSProperties = {
        width: "100%",
        padding: "12px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        backgroundColor: "white",
        fontSize: "13px",
        fontFamily: "monospace",
        color: "#374151",
        resize: "vertical",
        cursor: "text",
    };

    const models = parseModelsFromUrl(mealPlan.generation_url);

    return (
        <div style={containerStyle}>
            {/* Token Usage */}
            {mealPlan.token_count && (
                <div style={sectionStyle}>
                    <h2 style={titleStyle}>🪙 Token Usage</h2>
                    <div style={cardStyle}>
                        <div
                            style={{
                                fontSize: "24px",
                                fontWeight: "600",
                                color: "#7c3aed",
                                textAlign: "center",
                            }}
                        >
                            {mealPlan.token_count.toLocaleString()} tokens
                        </div>
                        <div
                            style={{
                                fontSize: "14px",
                                color: "#6b7280",
                                textAlign: "center",
                                marginTop: "4px",
                            }}
                        >
                            Used to generate this meal plan
                        </div>
                    </div>
                </div>
            )}

            {/* Model Settings */}
            {mealPlan.generation_url && (
                <div style={sectionStyle}>
                    <h2 style={titleStyle}>🤖 Model Settings</h2>
                    <div style={cardStyle}>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "16px",
                            }}
                        >
                            <div>
                                <div style={labelStyle}>Generator Model</div>
                                <div
                                    style={{
                                        padding: "8px 12px",
                                        backgroundColor: "#e0f2fe",
                                        border: "1px solid #7dd3fc",
                                        borderRadius: "6px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        color: "#0369a1",
                                    }}
                                >
                                    {getModelLabel(models.primaryModel)}
                                </div>
                            </div>
                            <div>
                                <div style={labelStyle}>Refinement Model</div>
                                <div
                                    style={{
                                        padding: "8px 12px",
                                        backgroundColor: "#f0fdf4",
                                        border: "1px solid #86efac",
                                        borderRadius: "6px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        color: "#166534",
                                    }}
                                >
                                    {getModelLabel(models.refinementModel)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Generation Prompt */}
            {mealPlan.generation_prompt && (
                <div style={sectionStyle}>
                    <h2 style={titleStyle}>📝 Generation Prompt</h2>
                    <div style={cardStyle}>
                        <textarea
                            value={mealPlan.generation_prompt}
                            readOnly
                            style={{
                                ...textareaStyle,
                                minHeight: "200px",
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Plan Constraints */}
            {mealPlanData.constraints && (
                <div style={sectionStyle}>
                    <h2 style={titleStyle}>📋 Plan Configuration</h2>
                    <div style={cardStyle}>
                        <textarea
                            value={JSON.stringify(
                                mealPlanData.constraints,
                                null,
                                2
                            )}
                            readOnly
                            style={{
                                ...textareaStyle,
                                minHeight: "150px",
                                backgroundColor: "#f9fafb",
                                fontSize: "12px",
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}; 